/** Client for the sales invoice chase log: who chased a customer for an overdue invoice, and when. */

import { API_BASE, getAuthHeaders } from '../utils/supabase/client';

export interface InvoiceChaseEvent {
  id: string;
  invoiceId: string;
  chasedByName: string;
  chasedAt: string;
  /** One line left by the chaser; empty when none was given. */
  note?: string;
}

/** Newest first. An empty list also covers a server whose chase table is not live yet. */
export async function listInvoiceChases(invoiceId: string): Promise<InvoiceChaseEvent[]> {
  const response = await fetch(`${API_BASE}/invoices/${encodeURIComponent(invoiceId)}/chase-events`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Could not load the chase log (${response.status})`);
  const body = (await response.json()) as { events?: InvoiceChaseEvent[] };
  return body.events ?? [];
}

export async function logInvoiceChase(invoiceId: string, note?: string): Promise<InvoiceChaseEvent> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/invoices/${encodeURIComponent(invoiceId)}/chase`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(note ? { note } : {}),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Could not log the chase (${response.status})`);
  }
  return (await response.json()) as InvoiceChaseEvent;
}

export interface LatestInvoiceChase {
  invoiceId: string;
  chasedAt: string;
  chasedByName: string;
}

/** The newest chase per invoice over the server's recent window. An empty list also covers a server whose chase table is not live yet. */
export async function listLatestInvoiceChases(workspaceId: string): Promise<LatestInvoiceChase[]> {
  const response = await fetch(`${API_BASE}/invoices/chase-events/latest?workspaceId=${encodeURIComponent(workspaceId)}`, {
    headers: await getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Could not load the latest chases (${response.status})`);
  const body = (await response.json()) as { latest?: LatestInvoiceChase[] };
  return body.latest ?? [];
}
